import React from 'react';
import { getApiBase } from '../apiBase';

const OCCASIONS = ['Winter special', 'Spring cleanup', 'Summer special', 'Fall maintenance', 'Holiday thank-you'];

const HOOKS = {
  'Winter special': 'Cold months are hard on your home, and we want to help you stay ahead of winter wear.',
  'Spring cleanup': 'Spring is the perfect time to refresh and tackle everything that piled up over winter.',
  'Summer special': 'Summer is here, so let us handle the heavy lifting while you enjoy the season.',
  'Fall maintenance': 'Before the weather turns, a little fall maintenance now saves you headaches later.',
  'Holiday thank-you': 'As the year winds down, we wanted to pause and say thank you for trusting us.',
};

function buildTemplate(occasion) {
  const safeOccasion = (typeof occasion === 'string' && occasion.trim()) ? occasion : OCCASIONS[0];
  const hook = HOOKS[safeOccasion] || 'We have something special for you this season.';
  const subject = `Subject: ${safeOccasion} from [Business name]`;
  const body =
    `Hi there,\n\n${hook} That's why [Business name] is offering a limited ${safeOccasion.toLowerCase()} just for our customers. ` +
    `Spots fill up fast this time of year, so now is the moment to lock in your spot.\n\n` +
    `Reply to this email or give us a call to book — we'd love to take care of you.\n\n— The team at [Business name]`;
  return `${subject}\n\n${body}`;
}

const inp = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', marginTop: 8,
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 10, color: '#fff', fontSize: 14,
};

export default function FollowupsPanel({ ownerId, onClose }) {
  const STORAGE_KEY = 'howto_followups_occasion';
  const [occasion, setOccasion] = React.useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || OCCASIONS[0]; } catch (_) { return OCCASIONS[0]; }
  });
  const [copied, setCopied] = React.useState(false);
  const [recipients, setRecipients] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const template = React.useMemo(() => buildTemplate(occasion), [occasion]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, occasion); } catch (_) {}
    setCopied(false);
    setResult(null);
  }, [occasion]);

  const handleCopy = React.useCallback(async () => {
    try { await navigator.clipboard.writeText(template); setCopied(true); }
    catch (_) { setCopied(false); }
  }, [template]);

  const sendToList = React.useCallback(async () => {
    if (sending) return; // guard against double-send (avoids emailing the list twice)
    setResult(null);
    const list = Array.from(new Set(
      String(recipients || '').split(/[\s,;]+/).map((s) => s.trim()).filter((s) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s))
    ));
    if (!list.length) { setResult({ ok: false, msg: 'Add at least one customer email above.' }); return; }
    // Split the template into subject + body.
    const lines = template.split('\n');
    const subject = (lines[0] || '').replace(/^Subject:\s*/i, '').trim() || `${occasion} from us`;
    const body = lines.slice(1).join('\n').trim();
    setSending(true);
    try {
      const res = await fetch(`${getApiBase()}/api/owner/followups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: list, subject, body, confirm: true }),
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data && data.ok) {
        const sent = typeof data.sent === 'number' ? data.sent : list.length;
        setResult({ ok: true, msg: `Sent to ${sent} customer${sent === 1 ? '' : 's'}.` });
      } else if (data && typeof data.sent === 'number' && data.sent > 0) {
        const failed = typeof data.failed === 'number' ? data.failed : Math.max(0, list.length - data.sent);
        setResult({ ok: true, msg: `Sent ${data.sent}, ${failed} failed (check your email sender setup).` });
      } else {
        const why = (data && (data.message || data.error || (data.errors && data.errors[0] && data.errors[0].error))) || `Failed (${res.status})`;
        setResult({ ok: false, msg: `Couldn't send — ${why}. Delivery needs your email sender (EMAIL_FROM) verified.` });
      }
    } catch (e) {
      setResult({ ok: false, msg: (e && e.message) ? e.message : 'Network error — could not reach the server.' });
    } finally {
      setSending(false);
    }
  }, [recipients, template, occasion, sending]);

  return (
    <div className="agent-detail" onClick={onClose}>
      <div className="agent-detail__panel" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="agent-detail__close" onClick={onClose} aria-label="Close">×</button>
        <div className="agent-detail__title">Customer follow-ups</div>
        <div className="agent-detail__cadence">Seasonal</div>
        <p className="agent-detail__desc">
          Aria writes seasonal promo emails; you review then send to your list.
        </p>
        <div className="agent-detail__section">Choose an occasion</div>
        <div className="sched-leadrow">
          <span className="sched-leadlabel">Occasion</span>
          <select
            className="sched-select"
            value={occasion}
            onChange={(e) => setOccasion(e.target.value)}
          >
            {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div
          style={{
            marginTop: 14, padding: '14px 16px', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
            color: 'rgba(255,255,255,0.96)', whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.5,
          }}
        >
          {template}
        </div>
        <button type="button" className="agent-detail__cta" style={{ marginTop: 14 }} onClick={handleCopy}>
          {copied ? 'Copied' : 'Copy email'}
        </button>

        <div className="agent-detail__section">Send to your list</div>
        <textarea
          style={{ ...inp, minHeight: 64, resize: 'vertical' }}
          placeholder="Customer emails, separated by commas or new lines"
          value={recipients}
          onChange={(e) => setRecipients(e.target.value)}
        />
        <button type="button" className="agent-detail__cta" style={{ marginTop: 10 }} disabled={sending} onClick={sendToList}>
          {sending ? 'Sending…' : 'Send to my list'}
        </button>
        {result && (
          <div className={`agent-detail__status ${result.ok ? 'sched-ok' : 'sched-warn'}`} style={{ marginTop: 10 }}>
            {result.msg}
          </div>
        )}

        <div className="agent-detail__note">
          Review it, then send. Large sends go out through your connected email provider.
        </div>
      </div>
    </div>
  );
}
